/**
 * Opening agent-written HTML and SVG without handing it the app's powers.
 *
 * A blob: URL made by the app page inherits the app's origin. Opening agent
 * HTML that way ran its scripts as ProClaw, able to call the API with the
 * user's session. Likewise SVG put into the page with innerHTML can run event
 * handlers (`onload`, `onerror`) in the page.
 */

function escapeAttr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

function escapeText(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Open HTML in a new tab inside a sandboxed iframe. The outer page has no
 * script of its own; the agent's HTML runs in the iframe with an opaque origin
 * (`sandbox="allow-scripts allow-modals"`, never `allow-same-origin`), the same rule as the
 * in-app preview.
 */
export function openHtmlInSandboxedTab(html: string, title = "Preview"): void {
  const page =
    "<!doctype html><html><head><meta charset=\"utf-8\">" +
    `<title>${escapeText(title)}</title>` +
    "<style>html,body{margin:0;height:100%;background:#fff}" +
    "iframe{border:0;display:block;width:100%;height:100%}</style>" +
    "</head><body>" +
    `<iframe sandbox="allow-scripts allow-modals" srcdoc="${escapeAttr(html)}"></iframe>` +
    "</body></html>";
  const url = URL.createObjectURL(new Blob([page], { type: "text/html" }));
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * An SVG as an image URL. Rendered through <img>, an SVG cannot run scripts or
 * event handlers, and it can be sized like any picture.
 */
export function svgToImageSrc(svg: string): string {
  const withNamespace = /<svg[^>]*\sxmlns=/.test(svg)
    ? svg
    : svg.replace(/<svg\b/, '<svg xmlns="http://www.w3.org/2000/svg"');
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(withNamespace)}`;
}
